/**
 * DynamicProductPricing Web Component
 * ============================================================
 * Propósito: Controla a exibição dinâmica de preços para produtos e kits com base em testes A/B via Statsig.
 *
 * Padrão de Arquitetura:
 * - Usa Statsig Layer para determinar o grupo de preço do usuário (price adjustment).
 * - Usa cookies para persistir os grupos atribuídos e evitar múltiplas requisições.
 * - Componentes dinâmicos respondem a eventos personalizados quando os dados estiverem prontos.
 *
 * Visão Geral da Lógica:
 * - Se for kit: itera produtos filhos, calcula os ajustes e aplica preço final.
 * - Se não for kit: busca grupo do Statsig e aplica ajuste ao preço do produto.
 * - Eventos 'productHandle-price-group-loaded' são emitidos para sincronização entre componentes.
 * - Campos hidden são gerados dinamicamente para o formulário de envio.
 *
 * Observabilidade:
 * - Usa Logger.log para debugging de carregamento e ajustes de preço.
 * - Usa addError para log de falhas ao carregar dados ou contexto Statsig.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getOrCreateStatsigManager, Logger, syncDebugFlag } from './statsig-manager.js';
const EXPERIMENT = {
    title: "DynamicProductPricing",
    name: "dynamic-product-pricing-v1",
    gate: "dynamic-product-pricing",
    layer: "dynamic-product-pricing",
};
const DEBUG = false; // Altere para false para desativar logs deste teste 
if (!customElements.get("dynamic-price-controller"))
    customElements.define("dynamic-price-controller", class DynamicProductPricing extends HTMLElement {
        /**
         * Construtor do Web Component DynamicProductPricing
         * ------------------------------------------------------------
         * Responsável por inicializar o componente e determinar se o produto
         * é um kit ou não, disparando o fluxo correto de inicialização.
         * O componente utiliza atributos da DOM para configurar estado inicial,
         * e inicia o carregamento dos grupos de preço dinâmicos via Statsig.
         */
        constructor() {
            super();
            this.loading = true;
            this.productHandle = this.dataset.productHandle;
            this.statsigLayer = `${this.productHandle}-price-test`;
            this.productPrice = parseInt(this.dataset.price);
            this.productCompareAtPrice = parseInt(this.dataset.compareAtPrice);
            this.priceGroups = this.dataset.priceGroups && JSON.parse(this.dataset.priceGroups);
            this.isInSale = this.productCompareAtPrice > this.productPrice;
            this.calcPriceComponents = this.querySelectorAll("calc-prices");
            this.priceGroup = null;
            this.cupomCountdown = this.querySelector("cupom-countdown");
            this.isOnCart = this.dataset.cartDiscount === "true";
            this.ddErrorsInfos = {
                stable_id: this.statsigStableId,
                layer: this.statsigLayer,
                category: "Statsig",
                component: `dynamic-price-controller`,
            };
            this.productCost = {
                price: this.productPrice,
                compareAtPrice: this.productCompareAtPrice,
            };
            this.priceAdjustment = 0;
            this.markdownAdjustment = 0;
            this.isKit = this.dataset.isKit === "true";
            this.setLoading(true);
            if (this.isKit) {
                this.initKit();
            }
            else {
                this.init();
            }
        }
        /**
         * initKit()
         * ----------------------------------------------
         * Fluxo de inicialização para produtos do tipo kit.
         * Itera sobre os produtos filhos, obtém os grupos de preço (Statsig)
         * de cada um e calcula o ajuste total do kit. Utiliza cookies para
         * persistência e sincronização.
         */
        initKit() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const productsWithPriceGroups = this.dataset.productsWithPriceGroups.split("|");
                    for (let i = 0; i < productsWithPriceGroups.length; i++) {
                        const product = productsWithPriceGroups[i];
                        const productSplit = product.split("::");
                        const productHandle = productSplit[0];
                        const productQnt = parseInt(productSplit[1]);
                        const productPriceGroup = JSON.parse(productSplit[2]);
                        const priceGroupCookie = this.getPriceGroupCookie(productHandle);
                        let markdownAdj = 0;
                        if (priceGroupCookie && priceGroupCookie !== "loading") {
                            this.priceAdjustment += priceGroupCookie.priceAdjustment * productQnt;
                            if (typeof priceGroupCookie.markdownAdjustment === "number") {
                                markdownAdj += priceGroupCookie.markdownAdjustment * productQnt;
                            }
                            else {
                                markdownAdj += priceGroupCookie.priceAdjustment * productQnt;
                            }
                        }
                        else {
                            // create loading cookie
                            this.createLoadingCookie(productHandle);
                            // get startsig group name
                            const priceGroupName = yield this.getStatsigData(productHandle);
                            // find price adjustment
                            const priceGroup = this.getPriceGroup(priceGroupName, productPriceGroup, productHandle);
                            if (!priceGroup)
                                throw new Error(">> Price group not found");
                            this.priceAdjustment += priceGroup.priceAdjustment * productQnt;
                            if (typeof priceGroup.markdownAdjustment === "number") {
                                markdownAdj += priceGroup.markdownAdjustment * productQnt;
                            }
                            else {
                                markdownAdj += priceGroup.priceAdjustment * productQnt;
                            }
                            // send success event
                            this.sendLoadedEvent(priceGroup, productHandle);
                        }
                        this.markdownAdjustment += markdownAdj;
                    }
                    // Ensure markdownAdjustment is at least priceAdjustment (for kits, sum-wise)
                    if (this.markdownAdjustment < this.priceAdjustment) {
                        this.markdownAdjustment = this.priceAdjustment;
                    }
                    const kitCost = this.calculateProductPrices(this.priceAdjustment, this.markdownAdjustment);
                    this.updateProductPrice({
                        id: "kit",
                        groupId: "kit",
                        priceAdjustment: this.priceAdjustment,
                        markdownAdjustment: this.markdownAdjustment,
                        cost: kitCost,
                    });
                    // Also sendLoadedEvent with id: "kit"
                    this.sendLoadedEvent({
                        id: "kit",
                        groupId: "kit",
                        priceAdjustment: this.priceAdjustment,
                        markdownAdjustment: this.markdownAdjustment,
                        cost: kitCost,
                    });
                    this.setLoading(false);
                }
                catch (e) {
                    // Logger.error(">>> Error controller", e);
                    Logger.error("[Statsig][dynamic-price-controller] Failed to initialize kit", e, this.ddErrorsInfos);
                    this.setLoading(false);
                }
            });
        }
        /**
         * init()
         * ----------------------------------------------
         * Fluxo de inicialização para produtos unitários (não-kit).
         * Busca ou aguarda o grupo Statsig via cookie, aplica ajuste de preço
         * e emite evento de sincronização. Em caso de erro, utiliza fallback.
         */
        init() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    syncDebugFlag(EXPERIMENT.name, DEBUG);
                    // check if price group is already set in cookie
                    const priceGroupCookie = this.getPriceGroupCookie(this.productHandle);
                    if (priceGroupCookie === "loading") {
                        // wait for price group to be loaded
                        this.setupListeners();
                        return;
                    }
                    if (priceGroupCookie) {
                        this.priceGroup = priceGroupCookie;
                        this.markdownAdjustment = typeof priceGroupCookie.markdownAdjustment === "number" ? priceGroupCookie.markdownAdjustment : priceGroupCookie.priceAdjustment;
                        this.priceGroup.cost = this.calculateProductPrices(priceGroupCookie.priceAdjustment, this.markdownAdjustment);
                    }
                    else {
                        // create loading cookie
                        this.createLoadingCookie(this.productHandle);
                        // get startsig group name
                        const priceGroupName = yield this.getStatsigData();
                        // find price adjustment
                        const priceGroup = this.getPriceGroup(priceGroupName);
                        if (!priceGroup)
                            throw new Error(">> Price group not found");
                        this.priceGroup = priceGroup;
                        this.markdownAdjustment = typeof priceGroup.markdownAdjustment === "number" ? priceGroup.markdownAdjustment : priceGroup.priceAdjustment;
                    }
                    // apply price adjustment
                    this.updateProductPrice(Object.assign(Object.assign({}, this.priceGroup), { markdownAdjustment: this.markdownAdjustment, cost: this.calculateProductPrices(this.priceGroup.priceAdjustment, this.markdownAdjustment) }));
                    // send success event
                    this.sendLoadedEvent(Object.assign(Object.assign({}, this.priceGroup), { markdownAdjustment: this.markdownAdjustment, cost: this.calculateProductPrices(this.priceGroup.priceAdjustment, this.markdownAdjustment) }));
                    // set loading to false
                    this.setLoading(false);
                }
                catch (e) {
                    // Logger.error(">>> Error controller", e);
                    Logger.error(EXPERIMENT.name, "[Statsig][dynamic-price-controller] Failed to initialize product", e, this.ddErrorsInfos);
                    // last case scenario: create cookie with default values to avoid inconsistency
                    const fallbackGroup = this.createPriceGroupCookie(this.productHandle, "default", 0);
                    // send fallback event
                    const fallbackEvent = this.sendLoadedEvent(fallbackGroup);
                    // Logger.log(EXPERIMENT.name, ">>>> Fallback event", fallbackEvent);
                    this.setLoading(false);
                }
            });
        }
        sendLoadedEvent(priceGroup, productHandle = null) {
            const handle = productHandle ? productHandle : this.productHandle;
            const isPersisted = this.getPriceGroupCookie(handle) !== null;
            Logger.log(EXPERIMENT.name, `[Statsig][dynamic-price-controller] Group loaded for '${handle}' (${isPersisted ? "persisted" : "new"}):`, priceGroup);
            return document.dispatchEvent(new CustomEvent(`${handle}-price-group-loaded`, {
                detail: Object.assign({}, priceGroup),
            }));
        }
        setupListeners() {
            document.addEventListener(`${this.productHandle}-price-group-loaded`, (e) => {
                const detail = e.detail;
                this.updateProductPrice(detail);
                this.setLoading(false);
            });
        }
        calculateProductPrices(priceAdjustment, markdownAdjustment) {
            // markdownAdjustment is optional, fallback to priceAdjustment if not provided
            let markdown = typeof markdownAdjustment === "number" ? markdownAdjustment : priceAdjustment;
            // markdown must always be >= priceAdjustment
            if (markdown < priceAdjustment) {
                markdown = priceAdjustment;
            }
            const newPrice = this.productPrice + priceAdjustment;
            const newCompareAtPrice = this.productCompareAtPrice + markdown;
            return {
                price: newPrice,
                compareAtPrice: newCompareAtPrice,
            };
        }
        createLoadingCookie(productHandle) {
            const cookieName = `statsig-group-${productHandle}`;
            const cookieValue = "loading";
            document.cookie = `${cookieName}=${cookieValue};path=/;`;
        }
        createPriceGroupCookie(productHandle, groupId, priceAdjustment, markdownAdjustment) {
            const cookieName = `statsig-group-${productHandle}`;
            // If markdownAdjustment is not provided, default to priceAdjustment
            let markdownAdj = typeof markdownAdjustment === "number" ? markdownAdjustment : priceAdjustment;
            if (markdownAdj < priceAdjustment)
                markdownAdj = priceAdjustment;
            const cost = this.calculateProductPrices(priceAdjustment, markdownAdj);
            const cookieObject = {
                groupId,
                priceAdjustment,
                markdownAdjustment: markdownAdj,
                cost,
                id: groupId,
            };
            const cookieValue = JSON.stringify(cookieObject);
            document.cookie = `${cookieName}=${cookieValue};path=/;`;
            return cookieObject;
        }
        getPriceGroupCookie(productHandle) {
            var _a;
            const cookieName = `statsig-group-${productHandle}`;
            const cookieValue = (_a = document.cookie
                .split("; ")
                .find((row) => row.startsWith(cookieName))) === null || _a === void 0 ? void 0 : _a.split("=")[1];
            if (!cookieValue)
                return null;
            if (cookieValue === "loading")
                return cookieValue;
            // Parse and return with markdownAdjustment if present
            Logger.log(EXPERIMENT.name, `[Statsig][dynamic-price-controller] Price group cookie found for '${productHandle}':`, cookieValue);
            return JSON.parse(cookieValue);
        }
        setLoading(loading) {
            this.loading = loading;
            if (loading) {
                this.style.opacity = "0";
            }
            else {
                this.style.opacity = "1";
            }
        }
        updateProductPrice(priceGroup) {
            var _a;
            // Use markdownAdjustment if provided, else fallback to priceAdjustment
            const markdownAdj = typeof priceGroup.markdownAdjustment === "number" ? priceGroup.markdownAdjustment : priceGroup.priceAdjustment;
            const cost = this.calculateProductPrices(priceGroup.priceAdjustment, markdownAdj);
            const newPrice = cost.price;
            const newPriceMoney = this.formatMoney(newPrice);
            const newCompareAtPrice = cost.compareAtPrice;
            const newCompareAtPriceMoney = this.formatMoney(newCompareAtPrice);
            // update final price
            // const priceComponents = this.querySelectorAll(".price");
            // priceComponents.forEach((component) => {
            //   if (component instanceof HTMLElement) {
            //     component.innerHTML = newPriceMoney;
            //     component.dataset!.value = String(newPrice);
            //   }
            // });
            // // update compare at price
            // const compareAtPriceComponents = this.querySelectorAll(".markdown-price");
            // compareAtPriceComponents.forEach((component) => {
            //   if (component instanceof HTMLElement) {
            //     component.innerHTML = newCompareAtPriceMoney;
            //     component.dataset!.value = String(newCompareAtPrice);
            //   }
            // });
            const priceNormalizeComponents = this.querySelector("price-normalize");
            if (priceNormalizeComponents) {
                priceNormalizeComponents.dataset.pristinePrice = String(newPrice);
                priceNormalizeComponents.dataset.compareAtPrice = String(newCompareAtPrice);
                priceNormalizeComponents.dataset.timestamp = `StatsigDyoTestProductPricing:updateProductPrice-${new Date().toISOString()}`;
            }
            // update calc prices
            // if (this.calcPriceComponents.length > 0) {
            //   this.calcPriceComponents.forEach((calcPrice) => {
            //     if (calcPrice instanceof HTMLElement && calcPrice.getAttribute("data-value")) {
            //       calcPrice.dataset!.value = String(newPrice);
            //     }
            //   });
            // }
            // update cart discount
            if (this.isOnCart) {
                const discountElement = this.querySelector(".cart-item__old-price");
                if (discountElement instanceof HTMLElement) {
                    if (newCompareAtPrice > newPrice) {
                        discountElement.innerHTML = newCompareAtPriceMoney;
                    }
                    else {
                        discountElement.innerHTML = "";
                    }
                }
            }
            const inputCompareAt = (_a = this.closest("product-form")) === null || _a === void 0 ? void 0 : _a.querySelector('input[name="properties[_compare_at_price]"]');
            if (inputCompareAt) {
                inputCompareAt.value = String(newCompareAtPrice / 100);
            }
            // If newCompareAtPrice > newPrice, update price wrappers and class
            // if (newCompareAtPrice > newPrice) {
            //   // Ocultar completamente o bloco .price__regular
            //   const regularPriceWrapper = this.querySelector(".price__regular");
            //   if (regularPriceWrapper instanceof HTMLElement) {
            //     regularPriceWrapper.style.display = "none";
            //   }
            //   // Exibir somente o bloco .price__sale
            //   const salePriceWrapper = this.querySelector(".price__sale");
            //   if (salePriceWrapper instanceof HTMLElement) {
            //     salePriceWrapper.style.display = "block";
            //     // Mostrar ou ocultar o preço regular duplicado dentro de .price__sale (.price-item--regular)
            //     const innerRegular = salePriceWrapper.querySelectorAll(".price-item--regular");
            //     innerRegular.forEach((el) => {
            //       if (el instanceof HTMLElement) {
            //         el.style.display = newCompareAtPrice > newPrice ? "inline" : "none";
            //       }
            //     });
            //   }
            //   // Adiciona classe de sale
            //   const priceWrapper = this.querySelector(".price");
            //   if (priceWrapper instanceof HTMLElement) {
            //     priceWrapper.classList.add("price--on-sale");
            //   }
            // }
            Logger.log(EXPERIMENT.name, `[Statsig][dynamic-price-controller] Price applied:`, {
                newPrice,
                newCompareAtPrice,
                priceGroup,
            });
        }
        formatMoney(cents) {
            const value = (cents / 100).toFixed(2).replace(".", ",");
            return `R$ ${value.replace(/\B(?=(\d{3})+(?!\d))/g, ".").split(",")[0]}`;
        }
        getPriceGroup(priceGroupName, kitPriceGroup = null, kitProductHandle = null) {
            var _a;
            // get price group by name or random if statsig group not provided
            const priceGroups = kitPriceGroup
                ? kitPriceGroup
                : this.priceGroups.filter(g => g.price_adjustment !== undefined);
            const productHandle = kitProductHandle ? kitProductHandle : this.productHandle;
            const priceGroup = priceGroupName
                ? priceGroups.find((group) => group.id === priceGroupName)
                : priceGroups[Math.floor(Math.random() * priceGroups.length)];
            if (!priceGroup)
                return null;
            // Compatível com código antigo:
            // priceAdjustment = priceGroup.price_adjustment * 100
            // markdownAdjustment = (priceGroup.markdown_price_adjustment ?? priceGroup.price_adjustment) * 100
            const priceAdjustment = priceGroup.price_adjustment * 100;
            const markdownPrice = (_a = priceGroup.markdown_price_adjustment) !== null && _a !== void 0 ? _a : priceGroup.price_adjustment;
            const markdownAdjustment = markdownPrice * 100;
            const cost = this.calculateProductPrices(priceAdjustment, markdownAdjustment);
            const groupObject = {
                groupId: priceGroup.id,
                priceAdjustment,
                markdownAdjustment,
                cost,
                id: priceGroup.id,
                price_adjustment: priceGroup.price_adjustment,
            };
            // create cookie
            this.createPriceGroupCookie(productHandle, priceGroup.id, priceAdjustment, markdownAdjustment);
            return groupObject;
        }
        /**
         * getStatsigData(productHandle?)
         * ----------------------------------------------------
         * Lógica de obtenção do grupo Statsig para o produto (ou kit).
         * Busca o layer Statsig correspondente ao handle do produto,
         * extrai o nome do grupo e armazena o stableID. Usa fallback para null.
         * O client é obtido diretamente da propriedade interna do experimentManager
         * devido a uma limitação temporária da tipagem da classe StatsigExperimentManager.
         */
        getStatsigData(productHandle) {
            return __awaiter(this, void 0, void 0, function* () {
                const handle = productHandle ? productHandle : this.productHandle;
                const statsigLayer = `${handle}-price-test`;
                try {
                    const experimentManager = yield getOrCreateStatsigManager();
                    const layer = yield experimentManager.getLayerParams(statsigLayer);
                    if (!layer)
                        return null;
                    const groupName = layer.get("groupName");
                    if (!groupName)
                        return null;
                    // Log Layer and GroupName
                    Logger.log(EXPERIMENT.name, `[Statsig][getStatsigData] Layer: ${statsigLayer}, GroupName: ${groupName}`);
                    // O client é uma propriedade interna acessada diretamente.
                    // Isso é uma limitação temporária da tipagem da classe StatsigExperimentManager.
                    const client = experimentManager.client;
                    const stableContext = yield (client === null || client === void 0 ? void 0 : client.getAsyncContext());
                    this.statsigStableId = stableContext === null || stableContext === void 0 ? void 0 : stableContext.stableID;
                    // Log StableID
                    Logger.log(EXPERIMENT.name, `[Statsig][getStatsigData] StableID:`, this.statsigStableId);
                    return groupName;
                }
                catch (error) {
                    Logger.error(EXPERIMENT.name, "[Statsig][dynamic-price-controller] Failed to get Statsig layer data", error);
                    return null;
                }
            });
        }
        getKitDiscount() {
            const kitPrice = this.productPrice + this.priceAdjustment;
            const kitCompareAtPrice = this.productCompareAtPrice + this.markdownAdjustment;
            const discount = Math.abs((kitPrice / kitCompareAtPrice) * 100 - 100);
            return discount;
        }
    });
if (!customElements.get("statsig-price-input"))
    customElements.define("statsig-price-input", class DynamicPriceInput extends HTMLElement {
        constructor() {
            super();
            this.productHandle = this.dataset.productHandle;
            this.productPriceGroup = null;
            this.ready = false;
            this.form = this.parentElement;
            this.submitButton = this.form ? this.form.querySelector('button[type="submit"]') : null;
            this.ddErrorsInfos = {
                stable_id: "",
                layer: `${this.productHandle}-price-test`,
                category: "Statsig",
                component: `statsig-price-input`,
            };
            this.init();
        }
        init() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    // get price group from cookie
                    const priceGroupCookie = this.getPriceGroupCookie(this.productHandle);
                    // wait for price group to be loaded
                    if (priceGroupCookie === "loading" || !priceGroupCookie) {
                        return this.setupListeners();
                    }
                    // check if price group it's the default
                    if (priceGroupCookie.groupId === "default") {
                        this.setReady(true);
                        return;
                    }
                    this.productPriceGroup = priceGroupCookie.groupId;
                    // generate inputs (always both, priceAdjustment defaults to 0 if undefined)
                    this.generateFormInput(this.productPriceGroup, priceGroupCookie.priceAdjustment, typeof priceGroupCookie.markdownAdjustment === "number" ? priceGroupCookie.markdownAdjustment : priceGroupCookie.priceAdjustment);
                    this.setReady(true);
                }
                catch (e) {
                    Logger.error(EXPERIMENT.name, ">>>", e);
                    this.setReady(true);
                }
            });
        }
        getPriceGroupCookie(productHandle) {
            var _a;
            const cookieName = `statsig-group-${productHandle}`;
            const cookieValue = (_a = document.cookie
                .split("; ")
                .find((row) => row.startsWith(cookieName))) === null || _a === void 0 ? void 0 : _a.split("=")[1];
            if (cookieValue === "loading")
                return "loading";
            if (!cookieValue)
                return null;
            // Parse and return with markdownAdjustment if present
            return JSON.parse(cookieValue);
        }
        setupListeners() {
            document.addEventListener(`${this.productHandle}-price-group-loaded`, (e) => {
                const { detail } = e;
                this.productPriceGroup = detail.groupId;
                this.init();
            });
        }
        setReady(ready) {
            this.ready = ready;
            if (this.submitButton) {
                this.submitButton.dataset.priceGroupLoaded = ready;
            }
        }
        generateFormInput(priceGroup, priceAdjustment = 0, markdownAdjustment) {
            // markdownAdjustment defaults to priceAdjustment if not provided
            const markdownAdj = typeof markdownAdjustment === "number" ? markdownAdjustment : priceAdjustment;
            this.innerHTML = `
          <input type="hidden" name="properties[_price_group]" value="${priceGroup}"/>
          <input type="hidden" name="properties[_adjustment]" value="${priceAdjustment}"/>
          <input type="hidden" name="properties[_markdown]" value="${markdownAdj}"/>
        `;
        }
    });
